import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { B3SelectItem } from 'src/app/shared/models/b3-select-item.model';
import { Subscription, catchError, finalize, map, of, timeout } from 'rxjs';
import { AddressUtil } from 'src/app/helpers/utils/address.util';
import { AddressService } from 'src/app/services/address/address.service';
import { CitiesResponse, ParticipantCities } from 'src/app/shared/interfaces/address';

@Component({
  standalone: false,
  selector: 'app-combo-uf-cidade',
  templateUrl: './combo-uf-cidade.component.html'
})
export class ComboUfCidadeComponent implements OnInit, OnDestroy, OnChanges {

  private static readonly CITY_LOAD_TIMEOUT_MS = 4000;

  @Input() form!: FormGroup;
  @Input() ufControl: string = 'uf';
  @Input() cityControl: string = 'city';
  @Input() ufLabel = 'UF';
  @Input() cityLabel = 'Município';
  @Input() ufPlaceholder = 'Selecione a UF';
  @Input() cityPlaceholder = 'Selecione o Município';
  @Input() disabled = false;

  @Output() selectionChange = new EventEmitter<{ uf: any; city: any }>();

  selectUF: B3SelectItem[] = [];
  selectCity: B3SelectItem[] = [];

  isCityDisabled = true;
  isLoadingCities = false;
  hasCityLoadError = false;

  private subs = new Subscription();
  private cityLoadSub?: Subscription;
  private addressUtil = new AddressUtil();
  /** Evita reload reentrante quando FormControlName reemite o mesmo valor durante o CD. */
  private activeUfAbbr = '';
  /**
   * Guarda o município a reaplicar após o reload da UF.
   * Necessário porque o select pode reemitir a UF (mesmo abbr, outro objeto) depois
   * de `resetCityState` zerar o control — sem isso o restore perde o valor.
   */
  private pendingCityRestore: any = null;
  private cityRestoreTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private addressService: AddressService) {}

  ngOnInit(): void {
    this.selectUF = (this.addressUtil.estadosBrasil ?? []).map(uf => ({
      ...uf,
      inputId: String((uf as any)?.inputId ?? (uf as any)?.data ?? '').toUpperCase()
    }));

    const cityCtrl = this.form.get(this.cityControl);
    const initialCityValue = cityCtrl?.value ?? null;
    if (cityCtrl) {
      if (!cityCtrl.disabled) {
        cityCtrl.disable({ emitEvent: false });
        this.isCityDisabled = true;
      }
      cityCtrl.reset(null, { emitEvent: false });
      this.subs.add(
        cityCtrl.valueChanges.subscribe(() => this.emitSelection())
      );
    }

    const ufCtrl = this.form.get(this.ufControl);
    if (ufCtrl) {
      this.subs.add(
        ufCtrl.valueChanges.subscribe((ufRaw: any) => {
          const cityVal = this.form.get(this.cityControl)?.value;
          if (cityVal) {
            this.pendingCityRestore = cityVal;
          }
          const cityToRestore = this.pendingCityRestore;
          this.handleUfChange(ufRaw, !!cityToRestore, cityToRestore);
        })
      );

      const initialUf = ufCtrl.value;
      if (initialUf) {
        if (initialCityValue) {
          this.pendingCityRestore = initialCityValue;
        }
        this.handleUfChange(initialUf, !!initialCityValue, initialCityValue);
      }
    }

    this.syncUfControlState();
    this.syncCityControlState();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['disabled'] && !changes['disabled'].firstChange) {
      if (this.disabled) {
        this.isCityDisabled = true;
      } else if (this.selectCity.length || this.hasCityLoadError) {
        this.isCityDisabled = false;
      }
      this.syncUfControlState();
      this.syncCityControlState();
    }
  }

  ngOnDestroy(): void {
    if (this.cityRestoreTimer != null) {
      clearTimeout(this.cityRestoreTimer);
      this.cityRestoreTimer = null;
    }
    this.cityLoadSub?.unsubscribe();
    this.subs.unsubscribe();
  }

  private handleUfChange(ufRaw: any, restoreCity: boolean = false, restoredCityValue: any = undefined): void {
    const ufAbbr = this.normalizeUfToAbbr(ufRaw);
    const cityCtrl = this.form.get(this.cityControl);
    const previousUfAbbr = this.activeUfAbbr;
    const isSameUf = !!ufAbbr && ufAbbr === previousUfAbbr;
    const isUfSwitch = !!previousUfAbbr && !!ufAbbr && !isSameUf;

    // Troca real de UF (SP → RJ): descarta município anterior.
    if (isUfSwitch) {
      this.pendingCityRestore = null;
      this.clearCityRestoreTimer();
    }

    const previousCityValue = isUfSwitch
      ? null
      : (restoredCityValue !== undefined
        ? restoredCityValue
        : (this.pendingCityRestore ?? cityCtrl?.value));
    const shouldRestoreCity = !isUfSwitch && (restoreCity || !!previousCityValue);

    if (shouldRestoreCity && previousCityValue) {
      this.pendingCityRestore = previousCityValue;
    }

    if (isSameUf) {
      // Reemit da mesma UF (ex.: select normaliza o objeto): não reinicia o load.
      if (!shouldRestoreCity) {
        return;
      }
      if (this.isLoadingCities) {
        return;
      }
      if (this.selectCity.length || this.hasCityLoadError) {
        this.scheduleCityRestore(cityCtrl, previousCityValue);
        return;
      }
    }

    if (!ufAbbr) {
      this.pendingCityRestore = null;
      this.clearCityRestoreTimer();
      this.activeUfAbbr = '';
      this.resetCityState(cityCtrl);
      this.emitSelection();
      return;
    }

    if (!shouldRestoreCity) {
      this.pendingCityRestore = null;
      this.clearCityRestoreTimer();
    }

    this.activeUfAbbr = ufAbbr;
    this.resetCityState(cityCtrl);

    this.isLoadingCities = true;
    this.syncCityControlState();

    this.cityLoadSub = this.addressService.getCitiesByAbbreviation(ufAbbr).pipe(
      timeout(ComboUfCidadeComponent.CITY_LOAD_TIMEOUT_MS),
      map((res: CitiesResponse) => res?.data?.participantCities ?? []),
      catchError(() => {
        this.hasCityLoadError = true;
        return of<ParticipantCities[]>([]);
      }),
      finalize(() => {
        this.isLoadingCities = false;
        this.syncCityControlState();
      })
    ).subscribe(cities => {
      if (this.isOutdatedCityResponse(ufAbbr)) {
        return;
      }

      this.selectCity = this.mapCitiesToOptions(cities);
      if (!this.selectCity.length) {
        this.hasCityLoadError = true;
      }
      this.updateCityAvailability();
      this.syncCityControlState();

      const cityToRestore = shouldRestoreCity
        ? (previousCityValue ?? this.pendingCityRestore)
        : null;

      // Aplica após o tick para o [options] do b3-input-select já ter atualizado.
      this.scheduleCityRestore(cityCtrl, cityToRestore);
    });
  }

  private scheduleCityRestore(
    cityCtrl: ReturnType<FormGroup['get']>,
    previousCityValue: any,
  ): void {
    this.clearCityRestoreTimer();

    const valueToRestore = previousCityValue ?? this.pendingCityRestore;
    if (!valueToRestore) {
      this.applyRestoredCityValue(cityCtrl, false, null);
      this.emitSelection();
      return;
    }

    this.pendingCityRestore = valueToRestore;
    // Aplica já (form) e de novo após options/enable do b3-input-select.
    this.applyRestoredCityValue(cityCtrl, true, valueToRestore, false);
    this.emitSelection();

    this.cityRestoreTimer = setTimeout(() => {
      this.cityRestoreTimer = null;
      const pending = this.pendingCityRestore ?? valueToRestore;
      this.applyRestoredCityValue(cityCtrl, true, pending, true);
      this.emitSelection();
    }, 50);
  }

  private clearCityRestoreTimer(): void {
    if (this.cityRestoreTimer != null) {
      clearTimeout(this.cityRestoreTimer);
      this.cityRestoreTimer = null;
    }
  }

  private resetCityState(cityCtrl: ReturnType<FormGroup['get']>): void {
    this.selectCity = [];
    this.hasCityLoadError = false;
    cityCtrl?.reset(null, { emitEvent: false });
    cityCtrl?.disable({ emitEvent: false });
    this.isCityDisabled = true;
    this.cityLoadSub?.unsubscribe();
  }

  private isOutdatedCityResponse(expectedUfAbbr: string): boolean {
    const currentUfVal = this.form.get(this.ufControl)?.value;
    const currentUfAbbr = this.normalizeUfToAbbr(currentUfVal);
    return currentUfAbbr !== expectedUfAbbr;
  }

  private mapCitiesToOptions(cities: ParticipantCities[]): B3SelectItem[] {
    return cities.map(city => ({
      inputId: String(city?.cityCode ?? ''),
      label: city?.cityName ?? '',
      data: String(city?.cityName ?? '')
    }));
  }

  private updateCityAvailability(): void {
    this.isCityDisabled = !((this.selectCity.length || this.hasCityLoadError) && !this.disabled);
  }

  private applyRestoredCityValue(
    cityCtrl: ReturnType<FormGroup['get']>,
    restoreCity: boolean,
    previousCityValue: any,
    clearPending = true,
  ): void {
    if (!restoreCity) {
      this.pendingCityRestore = null;
      cityCtrl?.setValue(null, { emitEvent: false });
      return;
    }

    if (!previousCityValue) {
      return;
    }

    // Garante que o CVA do select receba writeValue com o control habilitado.
    this.isCityDisabled = false;
    this.isLoadingCities = false;
    cityCtrl?.enable({ emitEvent: false });

    if (this.hasCityLoadError) {
      const manualCityValue = this.getManualCityValue(previousCityValue);
      if (manualCityValue) {
        cityCtrl?.setValue(manualCityValue, { emitEvent: false });
      }
      if (clearPending) {
        this.pendingCityRestore = null;
      }
      return;
    }

    const found = this.findCityOption(previousCityValue);
    if (found) {
      cityCtrl?.setValue({ ...found }, { emitEvent: false });
      if (clearPending) {
        this.pendingCityRestore = null;
      }
      return;
    }

    const previousLabel = this.getManualCityValue(previousCityValue);
    if (previousLabel) {
      const fallback: B3SelectItem = {
        inputId: previousLabel,
        label: previousLabel,
        data: previousLabel,
      };
      const alreadyInjected = this.selectCity.some(
        item => this.normalizeCityName(String(item.label ?? '')) === this.normalizeCityName(previousLabel),
      );
      if (!alreadyInjected) {
        this.selectCity = [fallback, ...this.selectCity];
      }
      cityCtrl?.setValue({ ...fallback }, { emitEvent: false });
    }

    if (clearPending) {
      this.pendingCityRestore = null;
    }
  }

  private findCityOption(previousCityValue: any): B3SelectItem | undefined {
    const currentCode = typeof previousCityValue === 'string'
      ? previousCityValue
      : String(previousCityValue?.inputId ?? '');
    const previousLabel = this.getManualCityValue(previousCityValue);
    const normalizedLabel = this.normalizeCityName(previousLabel);

    return this.selectCity.find(item => {
      if (currentCode && String(item.inputId) === currentCode) {
        return true;
      }

      if (!normalizedLabel) {
        return false;
      }

      return this.normalizeCityName(String(item.label ?? '')) === normalizedLabel
        || this.normalizeCityName(String(item.data ?? '')) === normalizedLabel;
    });
  }

  private getManualCityValue(previousCityValue: any): string {
    if (typeof previousCityValue === 'string') {
      return previousCityValue.trim();
    }

    return String(previousCityValue?.label ?? previousCityValue?.data ?? '').trim();
  }

  private normalizeCityName(value: string): string {
    return value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toUpperCase();
  }

  private normalizeUfToAbbr(val: any): string {
    if (!val) return '';
    if (typeof val === 'string') return val.trim().toUpperCase();
    return String(val?.inputId ?? '').trim().toUpperCase();
  }

  private syncUfControlState(): void {
    const ufCtrl = this.form.get(this.ufControl);
    if (this.disabled) {
      ufCtrl?.disable({ emitEvent: false });
      return;
    }

    ufCtrl?.enable({ emitEvent: false });
  }

  private syncCityControlState(): void {
    const cityCtrl = this.form.get(this.cityControl);
    if (this.disabled || this.isCityDisabled || this.isLoadingCities) {
      cityCtrl?.disable({ emitEvent: false });
      return;
    }

    cityCtrl?.enable({ emitEvent: false });
  }

  private emitSelection(): void {
    this.selectionChange.emit({
      uf: this.form.get(this.ufControl)?.value ?? null,
      city: this.form.get(this.cityControl)?.value ?? null
    });
  }
}
